/**
 * Colour-coded breakdown legend shared by the charts that plot one value per
 * category (ranked bars, single-series bars, donuts).
 *
 * Distinct from `SeriesLegend` in `cartesianChrome`, and the two are not
 * interchangeable. That one is an identity key for a MEASURE plotted across
 * many points, so it carries a label and nothing else -- a "value" column
 * would have to pick one of the points to show. This one keys a CATEGORY that
 * has exactly one value, so the row can carry the number and its share of the
 * total, which is most of why the legend is worth the width.
 *
 * Each row is tinted with its own category colour: the swatch, the hover wash,
 * the active border and the share pill all read from it, so a row is findable
 * by colour alone before any text is read.
 */

import type { CSSProperties } from "react";
import { cn } from "../ui/utils";
import { formatNumber } from "./chartUtils";
import { LegendPager } from "./LegendPager";

export interface BreakdownLegendItem {
  name: string;
  /** Accent for the row: swatch, hover wash and pill are all mixed from it. */
  color: string;
  value: number;
  /** Integer percent share of the visible total, supplied by the parent. */
  share: number;
  /** Toggled off: dimmed and struck through, and it ignores hover. */
  hidden?: boolean;
}

export interface ChartBreakdownLegendProps {
  items: BreakdownLegendItem[];
  /**
   * Draw each row's value, and its share, after the label. BOTH default off:
   * the legend says which colour means what, and the numbers are already on
   * the marks and in the tooltip. Turn them on only where the chart itself
   * cannot carry them.
   */
  showValue?: boolean;
  showShare?: boolean;
  /** Row to render active, mirroring the highlighted mark in the plot. */
  activeName?: string | null;
  /** Row click. Omit to render the legend as a static key. */
  onToggle?: (name: string) => void;
  /** Row hover: `entered` is true on enter, false on leave. */
  onHover?: (name: string, entered: boolean) => void;
  valueFormatter?: (value: number) => string;
  className?: string;
}

/**
 * Integer percent shares that always sum to 100 -- largest-remainder
 * (Hamilton). Rounding each share on its own drifts (40+31+28 = 99); the
 * leftover points go to the rows with the largest fractional parts.
 */
export const breakdownShares = (
  values: number[],
): number[] => {
  const total = values.reduce((sum, v) => sum + v, 0);
  if (total <= 0) return values.map(() => 0);
  const exact = values.map((v) => (v / total) * 100);
  const floors = exact.map((e) => Math.floor(e));
  let deficit = 100 - floors.reduce((sum, f) => sum + f, 0);
  const byRemainder = exact
    .map((e, i) => ({ i, frac: e - Math.floor(e) }))
    .sort((a, b) => b.frac - a.frac);
  const shares = [...floors];
  for (let k = 0; k < byRemainder.length && deficit > 0; k += 1, deficit -= 1) {
    shares[byRemainder[k].i] += 1;
  }
  return shares;
};

export function ChartBreakdownLegend({
  items,
  showValue = false,
  showShare = false,
  activeName = null,
  onToggle,
  onHover,
  valueFormatter = formatNumber,
  className,
}: ChartBreakdownLegendProps) {
  const interactive = Boolean(onToggle);

  return (
    <LegendPager className={className}>
      <ul
        // One left-aligned row that pages rather than wrapping: the legend
        // sits above the plot and starts on the same edge as the card title,
        // and staying one line keeps the plot at a fixed height.
        className="flex w-max flex-row flex-nowrap items-center gap-x-2"
        data-testid="chart-breakdown-legend"
      >
      {items.map((item) => {
        const active = activeName === item.name;
        const dimmed = Boolean(item.hidden);
        return (
          // `shrink-0`: on a single-line rail a compressible item defeats the
          // paging it exists to enable.
          <li key={item.name} className="shrink-0">
            <button
              type="button"
              disabled={!interactive}
              // The accent drives every tinted surface on the row, so one
              // custom property keeps them from drifting apart.
              style={{ "--swatch": item.color } as CSSProperties}
              onClick={() => onToggle?.(item.name)}
              onMouseEnter={() => !dimmed && onHover?.(item.name, true)}
              onMouseLeave={() => !dimmed && onHover?.(item.name, false)}
              aria-pressed={interactive ? !dimmed : undefined}
              title={
                interactive
                  ? `${dimmed ? "Show" : "Hide"} ${item.name}`
                  : undefined
              }
              className={cn(
                "flex items-center gap-2",
                // Only reserve the numeric columns' width when they are drawn.
                // Label-only rows shrink to their text, so a short legend does
                // not sit in a row of half-empty boxes.
                (showValue || showShare) && "min-w-[150px]",
                "rounded-md border border-transparent px-2 py-[3px] text-left transition-colors",
                interactive &&
                  "cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                !interactive && "cursor-default",
                // Tailwind cannot express a colour-mix against a runtime hue,
                // so the wash and border ride on the arbitrary-value syntax.
                (active || interactive) &&
                  "hover:border-[color-mix(in_srgb,var(--swatch)_34%,var(--border))] hover:bg-[color-mix(in_srgb,var(--swatch)_14%,transparent)]",
                active &&
                  "border-[color-mix(in_srgb,var(--swatch)_34%,var(--border))] bg-[color-mix(in_srgb,var(--swatch)_14%,transparent)]",
                dimmed && "opacity-45",
              )}
            >
              <span
                aria-hidden="true"
                className="size-2.5 shrink-0 rounded-[3px]"
                style={{
                  backgroundColor: dimmed ? "var(--muted-foreground)" : item.color,
                  boxShadow: dimmed
                    ? undefined
                    : `0 0 0 2px color-mix(in srgb, ${item.color} 20%, transparent)`,
                }}
              />
              <span
                className={cn(
                  "whitespace-nowrap text-xs font-semibold text-foreground",
                  dimmed && "line-through",
                )}
              >
                {item.name}
              </span>
              {showValue && (
                <span className="ml-auto text-right text-xs font-bold tabular-nums text-foreground">
                  {valueFormatter(item.value)}
                </span>
              )}
              {showShare && (
                <span
                  className="min-w-10 rounded-full border px-1.5 py-px text-center text-[11px] font-bold tabular-nums text-foreground"
                  style={{
                    backgroundColor: `color-mix(in srgb, ${item.color} 20%, transparent)`,
                    borderColor: `color-mix(in srgb, ${item.color} 32%, transparent)`,
                  }}
                >
                  {item.share}%
                </span>
              )}
            </button>
          </li>
        );
      })}
      </ul>
    </LegendPager>
  );
}
